'use client';

import { clsx } from 'clsx';
import * as m from 'motion/react-m';
import { FC, MouseEvent, useCallback, useState } from 'react';

import { IconCaretDown } from 'src/general/Icons/IconCaretDown';
import { pushToDataLayer } from 'src/utils/pushToDataLayer';

import { LazyMotionWrapper } from '../LazyMotionWrapper/LazyMotionWrapper';
import styles from './Accordion.module.scss';
import { AccordionItem } from './Accordion.type';

interface AccordionProps {
  className?: string;
  items: AccordionItem[];
  /** Name used to track GTM events */
  trackingName?: string;
  initialOpenIndexes?: number[];
}

export const Accordion: FC<AccordionProps> = ({ className, items, trackingName, initialOpenIndexes = [] }) => (
  <div className={clsx(styles.root, className)}>
    {items.map(({ id, header, content }, index) => {
      const initialOpenIndex = initialOpenIndexes.findIndex((value) => value === index);

      return (
        <AccordionSingle
          key={id}
          id={id}
          header={header}
          content={content}
          index={index}
          trackingName={trackingName}
          initialOpen={initialOpenIndex >= 0}
        />
      );
    })}
  </div>
);

interface AccordionItemProps extends AccordionItem, Pick<AccordionProps, 'trackingName'> {
  index: number;
  initialOpen?: boolean;
  handleAccordionToggle?: (isOpen: boolean) => void;
  testId?: string;
}

export const AccordionSingle: FC<AccordionItemProps> = ({
  id,
  header,
  content,
  trackingName,
  index,
  initialOpen = false,
  handleAccordionToggle,
  testId,
}) => {
  const idHeader = `accordion-${id}-header`;
  const accordionTestId = testId || (id === 'driveType' ? 'filter-drivetrain' : undefined);

  const [isOpen, setIsOpen] = useState<boolean>(initialOpen);

  const handleClick = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      e.preventDefault();
      const newIsOpen = !isOpen;
      if (trackingName) {
        pushToDataLayer({ event: 'accordion_click', name: trackingName, index, open: isOpen });
      }
      setIsOpen(newIsOpen);
      handleAccordionToggle?.(newIsOpen);
    },
    [trackingName, index, isOpen, setIsOpen],
  );

  return (
    <section className={clsx(styles.accordion, 'accordion', { [styles.expanded]: isOpen, expanded: isOpen })}>
      <details open={isOpen} className={styles.wrapper} id={idHeader} data-testid={accordionTestId}>
        <summary className={clsx(styles.summary, { [styles.summaryOpen]: isOpen })} onClick={handleClick}>
          <span className={clsx(styles.accordionHeader, 'accordionHeader', 'font-extrabold', 'c-fs-body1')}>
            {header}
          </span>
          <div className={styles.icon}>
            <IconCaretDown />
          </div>
        </summary>
        <LazyMotionWrapper>
          <m.div
            animate={isOpen ? 'open' : 'closed'}
            variants={{
              open: {
                opacity: 1,
                height: 'auto',
                transform: 'translate3d(0, 0, 0)',
              },
              closed: {
                opacity: 0,
                height: 0,
                transform: 'translate3d(0, -2rem, 0)',
              },
            }}
          >
            <div className={clsx(styles.content, 'accordionContent', 'font-normal', 'c-fs-body2')}>{content}</div>
          </m.div>
        </LazyMotionWrapper>
      </details>
    </section>
  );
};
